// ROUTING IMPORTS
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';

// CONTEXT IMPORTS
import { AuthProvider, useAuth } from './contexts/AuthContext';

// REACT QUERY IMPORTS
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { PromptContextProvider } from './contexts/PromptContext';

// GLOBAL ICON PROVIDER
import { IconoirProvider } from 'iconoir-react';

// DOCUMENT HEAD MANAGEMENT (Smart App Banner — see __root.tsx)
import { HelmetProvider } from 'react-helmet-async';

// TOAST NOTIFICATIONS
import { Toaster } from 'sonner';

// SENTRY IMPORTS
import * as Sentry from '@sentry/react';

// GLOBAL NOT FOUND PAGE
import { NotFound } from './NotFound';

const queryClient = new QueryClient();

// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { auth: undefined! },
  defaultNotFoundComponent: NotFound,
});

// Register things for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Firebase Auth error codes that are expected user interactions, not bugs.
// The login/concierge UI already handles these gracefully with user-facing alerts.
// Downgraded to 'info' (not dropped) so concierges can still review patterns.
// Refs: MIDEO-UNIFIED-12 (auth/user-not-found), MIDEO-UNIFIED-1S (auth/invalid-action-code)
const EXPECTED_AUTH_CODES = [
  'auth/user-not-found',
  'auth/wrong-password',
  'auth/invalid-credential',
  'auth/too-many-requests',
  'auth/invalid-action-code',
];

// Patterns matching third-party errors (browser extensions, injected scripts).
// These have no first-party stack frames and are not actionable.
// Dropped entirely (return null) — no diagnostic value.
// Refs: MIDEO-UNIFIED-25 (runtime.sendMessage from Safari extension), CU-86afxbu95
const THIRD_PARTY_ERROR_PATTERNS = [/runtime\.sendMessage/];

// Bot user-agent patterns. Stripe.js can't load in bot environments,
// producing unactionable "Failed to load Stripe.js" errors.
// Dropped entirely (return null) — no diagnostic value.
// Refs: MIDEO-UNIFIED-1P, CU-86afxbu95
const BOT_UA_PATTERNS = [/bot/i, /crawl/i, /spider/i, /slurp/i, /facebookexternalhit/i, /linkedinbot/i];

// INITIALIZE SENTRY — DISABLED IN LOCAL DEV (errors visible in browser devtools)
Sentry.init({
  enabled: import.meta.env.VITE_SENTRY_ENVIRONMENT !== 'local',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // ⚠️ IP ADDRESS — a determined PHI disclosure, not an incidental default.
  // §164.308(a)(1)(ii)(D) information system activity review. Determined
  // 2026-09-03 for the mobile app (CU-86akbcqv8) and extended to the portal
  // 2026-09-09 (CU-86akfpwj7): the international-access watcher groups by
  // SERVICE, not platform, so portal traffic reaches the same cloud functions
  // and raises the same alerts — but without this flag there was no Sentry-side
  // IP to pivot to. MIDEO-UNIFIED-AF is the worked example: the Sentry IP is
  // what separated a crawler from a patient.
  // ⚠️ TWO switches permit collection; either alone stops it. This flag sends
  // it; Sentry's "Prevent Storing of IP Addresses" must stay OFF to store it.
  // See docs/guides/sentry-phi-determinations.md before changing either.
  sendDefaultPii: true,
  tracePropagationTargets: [import.meta.env.VITE_CLOUDFUNCTIONS_URI],
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
    }),
    Sentry.browserTracingIntegration(),
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.browserProfilingIntegration(),
    Sentry.feedbackIntegration({
      colorScheme: 'system',
    }),
  ],
  beforeSend(event, hint) {
    const error = hint?.originalException;

    // Filter 1: Expected Firebase Auth errors (already handled in UI)
    if (error instanceof Error) {
      const code = (error as unknown as Record<string, unknown>).code;
      if (typeof code === 'string' && EXPECTED_AUTH_CODES.includes(code)) {
        event.level = 'info';
        event.tags = { ...event.tags, filtered_reason: 'expected_auth', auth_code: code };
        return event;
      }
    }

    // Filter 2: Third-party/browser extension errors with no first-party frames — drop entirely
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    const hasFirstPartyFrames = frames?.some((frame) => frame.filename && !frame.filename.includes('node_modules'));
    const errorValue = event.exception?.values?.[0]?.value ?? '';
    if (!hasFirstPartyFrames && THIRD_PARTY_ERROR_PATTERNS.some((pattern) => pattern.test(errorValue))) {
      return null;
    }

    // Filter 3: Stripe load failures from bots/crawlers (can't execute external JS) — drop entirely
    if (errorValue.includes('Failed to load Stripe.js') && BOT_UA_PATTERNS.some((p) => p.test(navigator.userAgent))) {
      return null;
    }

    return event;
  },
  // SESSION REPLAY
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // BROWSER TRACING
  tracesSampleRate: 1.0,
  // BROWSER PROFILING (CHROME ONLY)
  profilesSampleRate: 1.0,
});

function InnerApp() {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ auth }} />;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <PromptContextProvider>
            <IconoirProvider
              iconProps={{
                color: 'rgb(3 105 161)',
                strokeWidth: 2,
                width: '2em',
                height: '2em',
              }}
            >
              <InnerApp />
            </IconoirProvider>
          </PromptContextProvider>
          <Toaster position="top-right" richColors />
        </QueryClientProvider>
      </AuthProvider>
      {/* {import.meta.env.VITE_ENV === 'LOCAL' ||
        (import.meta.env.VITE_ENV === 'staging' && (
          <ReactQueryDevtools initialIsOpen={false} position="bottom" buttonPosition="top-right" />
        ))} */}
    </HelmetProvider>
  );
}

export default App;
